import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart, LineElement, PointElement, CategoryScale, LinearScale,
  Tooltip, Legend, Filler, LineController,
} from 'chart.js';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChartConfiguration = any;
import { api } from '../api/client';
import TablePagination from '../components/TablePagination';
import { fmtMoney, fmtInt, fmtDays, fmtDateTime } from '../lib/fmt';
import { textAccentClass } from '../lib/ui';
import type { PaginatedResponse, SnapshotDetailOut, SnapshotOut } from '../types/api';

Chart.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler, LineController);
Chart.defaults.color = '#8A9BB8';
Chart.defaults.borderColor = '#22324F';

function CanvasChart({ id, cfg }: { id: string; cfg: ChartConfiguration }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const inst = useRef<Chart | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    inst.current?.destroy();
    inst.current = new Chart(ref.current, cfg);
    return () => { inst.current?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cfg.data)]);
  return <canvas ref={ref} id={id} height={150} />;
}

function SnapshotDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isLoading } = useQuery<SnapshotDetailOut>({
    queryKey: ['snapshot-detail', id],
    queryFn: () => api.get<SnapshotDetailOut>(`/historico/${id}`).then(r => r.data),
  });
  return (
    <div className="border border-mbc-line rounded-2xl bg-mbc-panel2 p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold">Snapshot de {data ? fmtDateTime(data.snapshot_at) : '…'}</h3>
        <button onClick={onClose} className="text-mbc-mute hover:text-white text-sm">✕ fechar</button>
      </div>
      {isLoading && <div className="text-mbc-mute text-sm">Carregando…</div>}
      {data?.kpis && (
        <>
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            {[
              { label: 'SKUs',            value: fmtInt(data.kpis.total_skus),       accent: 'mbc-orange' },
              { label: 'Zerados',         value: fmtInt(data.kpis.zerados),          accent: 'rose-400' },
              { label: 'Críticos',        value: fmtInt(data.kpis.criticos),         accent: 'amber-400' },
              { label: 'Excesso',         value: fmtInt(data.kpis.excesso),          accent: 'slate-400' },
              { label: 'Valor perdido',   value: fmtMoney(data.kpis.valor_perdido),  accent: 'rose-300' },
              { label: 'Valor sugest.',   value: fmtMoney(data.kpis.valor_sugestao), accent: 'mbc-bluel' },
            ].map(({ label, value, accent }) => (
              <div key={label} className="rounded-lg border border-mbc-line bg-mbc-panel p-3">
                <div className="text-[10px] text-mbc-mute uppercase font-bold">{label}</div>
                <div className={`text-xl font-extrabold mt-0.5 ${textAccentClass(accent)}`}>{value}</div>
              </div>
            ))}
          </div>
          {data.analysis_config && (
            <div className="mb-4 flex flex-wrap gap-3 text-xs text-mbc-mute">
              <span>Janela <strong className="text-mbc-soft">{(data.analysis_config.sales_window_days as number)}d</strong></span>
              <span>Horizonte <strong className="text-mbc-soft">{(data.analysis_config.horizon_days as number)}d</strong></span>
              <span>Margem mín. <strong className="text-mbc-soft">{(((data.analysis_config.min_margin as number)) * 100).toFixed(0)}%</strong></span>
            </div>
          )}
          <div className="rounded-xl border border-mbc-line overflow-hidden">
            <div className="px-3 py-2 bg-mbc-panel border-b border-mbc-line">
              <span className="text-xs text-mbc-mute font-bold uppercase">Top 50 — Valor perdido</span>
            </div>
            <div className="scroller" style={{ maxHeight: '40vh' }}>
              <table className="dt">
                <thead>
                  <tr>
                    <th style={{width:90}}>SKU</th>
                    <th className="text-left" style={{width:220}}>Produto</th>
                    <th className="text-center" style={{width:52}}>Cls</th>
                    <th className="num" style={{width:90}}>Vend. 90d</th>
                    <th className="num" style={{width:80}}>Estoque</th>
                    <th className="num" style={{width:95}}>Cobert.</th>
                    <th className="num" style={{width:90}}>Sugestão</th>
                    <th className="num" style={{width:120}}>Valor perdido</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_items.map(i => (
                    <tr key={i.sku}>
                      <td><span className="font-mono text-mbc-bluel text-xs">{i.sku}</span></td>
                      <td><span className="truncate inline-block max-w-[200px]" title={i.nome}>{i.nome}</span></td>
                      <td className="text-center"><span className="badge bg-mbc-orange/25 text-mbc-orangel">{i.classe}</span></td>
                      <td className="num">{fmtInt(i.vendas_90d)}</td>
                      <td className="num">{fmtInt(i.estoque_total)}</td>
                      <td className="num">{i.cobertura_dias!=null?`${i.cobertura_dias}d`:'—'}</td>
                      <td className="num text-emerald-300">{fmtInt(i.sugestao_compra)}</td>
                      <td className="num text-rose-300">{fmtMoney(Number(i.valor_perdido))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Historico() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const { data: snapshotsPage, isLoading } = useQuery<PaginatedResponse<SnapshotOut>>({
    queryKey: ['snapshots', page],
    queryFn: () => api.get<PaginatedResponse<SnapshotOut>>(`/historico?limit=${pageSize}&offset=${(page - 1) * pageSize}`).then(r => r.data),
  });
  const snapshots = snapshotsPage?.items ?? [];

  // Charts config — only when we have snapshots with KPIs
  const snapsWithKpis = [...snapshots].reverse().filter(s => s.kpis);
  const labels = snapsWithKpis.map(s => new Date(s.snapshot_at).toLocaleDateString('pt-BR'));

  const riscoCfg: ChartConfiguration = {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Fat. em risco',
        data: snapsWithKpis.map(s => Math.round(s.kpis!.valor_perdido)),
        borderColor: '#e0457b', backgroundColor: 'rgba(224,69,123,.15)', fill: true, tension: 0.3,
      }],
    },
    options: {
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => fmtMoney(ctx.raw as number) } } },
      scales: { y: { ticks: { callback: (v: any) => `R$${(Number(v)/1000).toFixed(0)}k` } } },
    },
  };

  const investCfg: ChartConfiguration = {
    type: 'line',
    data: {
      labels,
      datasets: [
        { label: 'Compra sugerida (R$)', data: snapsWithKpis.map(s => Math.round(s.kpis!.valor_sugestao)), borderColor: '#F47A20', backgroundColor: 'rgba(244,122,32,.15)', fill: true, tension: 0.3, yAxisID: 'y' },
        { label: 'Itens críticos', data: snapsWithKpis.map(s => s.kpis!.criticos), borderColor: '#1E6FD6', tension: 0.3, yAxisID: 'y1' },
      ],
    },
    options: {
      plugins: { legend: { labels: { boxWidth: 10 } } },
      scales: {
        y:  { position: 'left',  ticks: { callback: (v: any) => `R$${(Number(v)/1000).toFixed(0)}k` } },
        y1: { position: 'right', grid: { drawOnChartArea: false } },
      },
    },
  };

  return (
    <div className="space-y-6 fade-in">

      {openId !== null && <SnapshotDetail id={openId} onClose={() => setOpenId(null)} />}
      {isLoading && <div className="text-mbc-mute">Carregando…</div>}

      {!isLoading && snapshots.length === 0 && (
        <div className="card p-10 text-center">
          <div className="text-5xl mb-3">🗂️</div>
          <h3 className="text-white font-bold text-lg">Nenhum snapshot salvo</h3>
          <p className="text-mbc-mute text-sm mt-1">Processe os dados e clique em <strong>Salvar snapshot do dia</strong>.</p>
        </div>
      )}

      {snapsWithKpis.length >= 2 && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="card p-5">
            <h3 className="text-white font-bold mb-2">Evolução — Faturamento em risco</h3>
            <CanvasChart id="hRisco" cfg={riscoCfg} />
          </div>
          <div className="card p-5">
            <h3 className="text-white font-bold mb-2">Evolução — Compra sugerida &amp; itens críticos</h3>
            <CanvasChart id="hInvest" cfg={investCfg} />
          </div>
        </div>
      )}

      {snapshots.length > 0 && (
        <div className="card p-5">
          <h3 className="text-white font-bold mb-3">Snapshots salvos</h3>
          <div className="scroller">
            <table className="dt">
              <thead>
                <tr>
                  <th className="text-left" style={{width:160}}>Data</th>
                  <th className="num" style={{width:130}}>Compra sugerida</th>
                  <th className="num" style={{width:130}}>Fat. em risco</th>
                  <th className="num" style={{width:80}}>Críticos</th>
                  <th className="num" style={{width:80}}>Zerados</th>
                  <th className="num" style={{width:110}}>Cobertura média</th>
                  <th className="num" style={{width:60}}>SKUs</th>
                </tr>
              </thead>
              <tbody>
                {[...snapshots].map(s => (
                  <tr key={s.id} className="cursor-pointer" onClick={() => setOpenId(openId === s.id ? null : s.id)}>
                    <td className={`font-semibold ${openId===s.id?'text-mbc-orange':'text-white'}`}>
                      {fmtDateTime(s.snapshot_at)} {openId===s.id?'▲':'▼'}
                    </td>
                    <td className="num">{s.kpis ? fmtMoney(s.kpis.valor_sugestao) : '—'}</td>
                    <td className="num text-rose-300">{s.kpis ? fmtMoney(s.kpis.valor_perdido) : '—'}</td>
                    <td className="num text-amber-300">{s.kpis ? fmtInt(s.kpis.criticos) : '—'}</td>
                    <td className="num text-rose-300">{s.kpis ? fmtInt(s.kpis.zerados) : '—'}</td>
                    <td className="num">{s.kpis ? fmtDays(s.kpis.cobertura_media) : '—'}</td>
                    <td className="num">{fmtInt(s.items_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination total={snapshotsPage?.total ?? 0} page={page} pageSize={pageSize} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
